import { companiesApi, matricesApi, meApi } from '@/lib/api';
import { resolveLanding } from '@/lib/landing';
import type { MatrixWithDimensions } from '@mss_creator/shared';
import {
  Breadcrumb,
  type BreadcrumbItem,
  Button,
  type RenderLink,
  TextField,
} from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useMatches } from '@tanstack/react-router';
import {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';

export interface BreadcrumbCrumbsProps {
  params: Record<string, string>;
}

/**
 * `params` is typed as a generic `Record<string, string>` (the whole point
 * of dispatching crumb components across heterogeneous routes — see
 * `Breadcrumbs` below), and this project's `noUncheckedIndexedAccess` makes
 * every access on that shape `string | undefined`. Each call site below
 * knows which key its own route guarantees (`companyId` on
 * `/companies/$companyId`, `matrixId` on the three matrix routes) — a
 * missing key would mean a crumb component got wired to the wrong route,
 * a real bug worth failing loudly on rather than silently coercing.
 */
function requiredParam(params: Record<string, string>, key: string): string {
  const value = params[key];
  if (value === undefined) {
    throw new Error(`Breadcrumb: missing route param "${key}"`);
  }
  return value;
}

interface RouteStaticDataWithBreadcrumb {
  breadcrumb: ComponentType<BreadcrumbCrumbsProps>;
}

/**
 * `staticData` isn't registered with the router's global type generics
 * (BUILD-770 didn't warrant that wiring for 4 routes), so it comes back
 * loosely typed from `useMatches()` — narrow it here, once, instead of
 * reaching for `any` at every call site.
 */
function getBreadcrumbComponent(
  staticData: unknown,
): ComponentType<BreadcrumbCrumbsProps> | undefined {
  if (
    typeof staticData === 'object' &&
    staticData !== null &&
    'breadcrumb' in staticData &&
    typeof (staticData as { breadcrumb: unknown }).breadcrumb === 'function'
  ) {
    return (staticData as RouteStaticDataWithBreadcrumb).breadcrumb;
  }
  return undefined;
}

interface RouteStaticDataWithAction {
  action: ComponentType<BreadcrumbCrumbsProps>;
}

/** Same loose-`staticData` narrowing as `getBreadcrumbComponent`, for the
 *  optional right-aligned action a route may render in the crumb strip. */
function getActionComponent(staticData: unknown): ComponentType<BreadcrumbCrumbsProps> | undefined {
  if (
    typeof staticData === 'object' &&
    staticData !== null &&
    'action' in staticData &&
    typeof (staticData as { action: unknown }).action === 'function'
  ) {
    return (staticData as RouteStaticDataWithAction).action;
  }
  return undefined;
}

/** Router link for the package's `Breadcrumb` (paths are already resolved). */
const renderRouterLink: RenderLink = ({ href, children, ...rest }) => (
  <Link {...({ to: href, ...rest } as unknown as ComponentProps<typeof Link>)}>{children}</Link>
);

/** Label of a segment whose data is still loading. */
function LoadingLabel() {
  return (
    <>
      <span aria-hidden="true">…</span>
      <span className="sr-only">Chargement</span>
    </>
  );
}

/**
 * Root segments (« Entreprises », or nothing for a company-scoped user)
 * computed once by `Breadcrumbs` and prepended by every route's trail.
 */
const RootItemsContext = createContext<BreadcrumbItem[]>([]);

/**
 * Renders the package `Breadcrumb` for a route's own segments, after the
 * root segments provided by `Breadcrumbs`. `after` sits next to the trail
 * (the matrix rename button, for instance).
 */
export function BreadcrumbTrail({ items, after }: { items: BreadcrumbItem[]; after?: ReactNode }) {
  const rootItems = useContext(RootItemsContext);
  return (
    <div className="group flex min-w-0 items-center gap-2">
      <Breadcrumb items={[...rootItems, ...items]} renderLink={renderRouterLink} />
      {after}
    </div>
  );
}

/**
 * Centralized breadcrumb strip, mounted once in `__root.tsx` above `main`
 * (BUILD-770 — resolves the 2026-08-09 export-page design doc's decision 12).
 * A route opts in by declaring `staticData.breadcrumb`; routes that don't
 * (the company grid, admin) render nothing here.
 *
 * The root itself never unmounts across navigations, so `<DeepestCrumbs>` is
 * rendered through a variable holding the matched route's own component
 * (not a locally-defined one) and keyed by that route's id — React treats a
 * changed element type/key as a different subtree and remounts it cleanly,
 * which avoids the different routes' crumb components calling a different
 * number/order of hooks within what would otherwise look like one long-lived
 * component instance.
 */
export function Breadcrumbs() {
  const matches = useMatches();
  const deepest = [...matches].reverse().find((match) => getBreadcrumbComponent(match.staticData));
  const DeepestCrumbs = deepest ? getBreadcrumbComponent(deepest.staticData) : undefined;
  const DeepestAction = deepest ? getActionComponent(deepest.staticData) : undefined;

  const meQuery = useQuery({ queryKey: ['me'], queryFn: meApi.get });
  const isScopedRole = meQuery.data?.role === 'user' || meQuery.data?.role === 'admin';
  const membershipQuery = useQuery({
    queryKey: ['me', 'company'],
    queryFn: meApi.getMembership,
    enabled: isScopedRole,
  });

  if (!DeepestCrumbs || !deepest) return null;

  // Tri-state: `undefined` while role/membership are still resolving (render
  // a loading label rather than guess), then whatever `resolveLanding` — the
  // same function `/`'s own loader uses — would actually do for this user.
  const roleDataReady = meQuery.data !== undefined && (!isScopedRole || membershipQuery.isFetched);
  const showRoot = roleDataReady
    ? resolveLanding(
        meQuery.data as NonNullable<typeof meQuery.data>,
        membershipQuery.data?.companyId ?? null,
      ).to === '/'
    : undefined;

  const rootItems: BreadcrumbItem[] =
    showRoot === false
      ? []
      : [
          showRoot === undefined
            ? { label: <LoadingLabel /> }
            : { label: 'Entreprises', href: '/' },
        ];

  return (
    <div className="-mt-4 flex shrink-0 items-center justify-between gap-4 py-1">
      <RootItemsContext.Provider value={rootItems}>
        <DeepestCrumbs key={deepest.routeId} params={deepest.params} />
      </RootItemsContext.Provider>
      {DeepestAction && <DeepestAction key={`${deepest.routeId}:action`} params={deepest.params} />}
    </div>
  );
}

// ── Shared data for the company → matrix hierarchy ──────────────────────────

function useMatrixCompanyCrumb(matrixId: string) {
  const matrixQuery = useQuery({
    queryKey: ['matrix', matrixId],
    queryFn: () => matricesApi.get(matrixId),
  });
  const companyId = matrixQuery.data?.companyId;
  const companyQuery = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.getById(companyId ?? ''),
    enabled: companyId !== undefined,
  });
  return {
    companyId,
    companyName: companyQuery.data?.name,
    matrixTitle: matrixQuery.data ? (matrixQuery.data.sujet ?? 'Matrice sans sujet') : undefined,
  };
}

function companyItem(
  companyId: string | undefined,
  companyName: string | undefined,
): BreadcrumbItem {
  return companyId === undefined || companyName === undefined
    ? { label: <LoadingLabel /> }
    : { label: companyName, href: `/companies/${companyId}` };
}

function matrixItem(
  matrixId: string,
  matrixTitle: string | undefined,
  asLink: boolean,
): BreadcrumbItem {
  if (matrixTitle === undefined) return { label: <LoadingLabel /> };
  return asLink ? { label: matrixTitle, href: `/matrices/${matrixId}` } : { label: matrixTitle };
}

// ── Per-route crumb components (registered via each route's staticData) ─────

export function CompanySpaceBreadcrumb({ params }: BreadcrumbCrumbsProps) {
  const companyId = requiredParam(params, 'companyId');
  const companyQuery = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.getById(companyId),
  });
  return (
    <BreadcrumbTrail
      items={[{ label: companyQuery.data ? companyQuery.data.name : <LoadingLabel /> }]}
    />
  );
}

/** The archives page (issue #20): « Entreprises › Archives », the root crumb coming from `Breadcrumbs`. */
export function ArchivesBreadcrumb() {
  return <BreadcrumbTrail items={[{ label: 'Archives' }]} />;
}

/** Shared by the wizard and the grid page — both are a leaf, Company / {matrix title}. */
function CompanyThenMatrixPage({ params }: BreadcrumbCrumbsProps) {
  const matrixId = requiredParam(params, 'matrixId');
  const { companyId, companyName, matrixTitle } = useMatrixCompanyCrumb(matrixId);
  return (
    <BreadcrumbTrail
      items={[companyItem(companyId, companyName), matrixItem(matrixId, matrixTitle, false)]}
    />
  );
}

export const WizardBreadcrumb = CompanyThenMatrixPage;

function useRenameMatrix(matrixId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sujet: string) => matricesApi.patch(matrixId, { sujet }),
    onSuccess: (updated) => {
      queryClient.setQueryData<MatrixWithDimensions>(['matrix', matrixId], (previous) =>
        previous ? { ...previous, sujet: updated.sujet } : previous,
      );
    },
  });
}

/**
 * The matrix view's own leaf crumb (point 1, 2026-08-18 matrix-view grill) —
 * unlike `WizardBreadcrumb`'s plain read-only leaf, this one is the ONLY
 * place the matrix name shows on this route (the page itself renders no
 * separate `<h1>`), with a hover-revealed pencil button next to the trail
 * opening inline rename. Fully self-contained: reuses
 * `useMatrixCompanyCrumb`'s existing data-fetch for display and owns its own
 * rename mutation — no callback threading from the page, which never mounts
 * this component directly (it's dispatched via `staticData.breadcrumb` from
 * `Breadcrumbs`, itself in `__root.tsx`).
 */
export function MatrixBreadcrumb({ params }: BreadcrumbCrumbsProps) {
  const matrixId = requiredParam(params, 'matrixId');
  const { companyId, companyName, matrixTitle } = useMatrixCompanyCrumb(matrixId);
  const renameMutation = useRenameMatrix(matrixId);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  function startEditing() {
    setDraft(matrixTitle ?? '');
    setIsEditing(true);
  }

  function commit() {
    setIsEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== matrixTitle) {
      renameMutation.mutate(trimmed);
    }
  }

  function cancel() {
    setIsEditing(false);
    setDraft(matrixTitle ?? '');
  }

  const leaf: BreadcrumbItem =
    matrixTitle !== undefined && isEditing
      ? {
          label: (
            <TextField
              label={<span className="sr-only">Renommer la matrice</span>}
              // biome-ignore lint/a11y/noAutofocus: opening the inline rename input must place focus in it immediately, matching the existing inline-edit pattern (matrix-grid.tsx's cell editor).
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commit();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  cancel();
                }
              }}
            />
          ),
        }
      : matrixItem(matrixId, matrixTitle, false);

  return (
    <BreadcrumbTrail
      items={[companyItem(companyId, companyName), leaf]}
      after={
        matrixTitle !== undefined &&
        !isEditing && (
          <span
            data-testid="rename-matrix-control"
            className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
          >
            <Button
              size="icon"
              variant="ghost"
              icon="edit"
              aria-label="Renommer la matrice"
              onClick={startEditing}
            />
          </span>
        )
      }
    />
  );
}

export function ExportBreadcrumb({ params }: BreadcrumbCrumbsProps) {
  const matrixId = requiredParam(params, 'matrixId');
  const { companyId, companyName, matrixTitle } = useMatrixCompanyCrumb(matrixId);
  return (
    <BreadcrumbTrail
      items={[
        companyItem(companyId, companyName),
        matrixItem(matrixId, matrixTitle, true),
        { label: 'Export' },
      ]}
    />
  );
}

export function SettingsBreadcrumb() {
  return <BreadcrumbTrail items={[{ label: 'Réglages' }]} />;
}
